import { concat } from '@ember/helper';
import type RouterService from '@ember/routing/router-service';
import type Transition from '@ember/routing/transition';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type { ValidatorMap } from 'ember-changeset/types';
import { restartableTask, timeout } from 'ember-concurrency';
import type IntlService from 'ember-intl/services/intl';
import and from 'ember-truth-helpers/helpers/and';
import eq from 'ember-truth-helpers/helpers/eq';
import SearchableField from 'parog-web/components/care-giver/new/searchable-field';
import CrudJournalEventBaseComponent from 'parog-web/components/forc/abstract/crud-journal-event-based-component';
import Provider from 'parog-web/components/forc/provider';
import JournalEventRetrievalStrategies from 'parog-web/components/forc/utils/journal-event-retrieval-strategies';
import ProductName from 'parog-web/components/product-name';
import tWithDefault from 'parog-web/helpers/t-with-default';
import type CareGiver from 'parog-web/models/care-giver';
import CareGiverAddedJournalEvent, {
  CareGiverRoles
} from 'parog-web/models/care-giver-added-journal-event';
import type FetcherService from 'parog-web/services/fetcher';
import type SemeiaSessionService from 'parog-web/services/session';
import { fillPhoneInput } from 'parog-web/utils/phone';

export default class NewCareGiverFormComponent extends CrudJournalEventBaseComponent {
  static override forcDisplayName = "Nouveau membre de l'entourage de soin";
  static override fullComponentId = 'care-giver/new';
  static override journalEventClass = CareGiverAddedJournalEvent;

  @service declare fetcher: FetcherService;
  @service declare intl: IntlService;
  @service declare router: RouterService;
  @service declare session: SemeiaSessionService;

  @tracked isCareGiverSelected = false;
  @tracked searchedBy?: string;

  override retrieveJournalEvent =
    JournalEventRetrievalStrategies.sourceJournalEvent;
  roleOptions = CareGiverRoles;

  get shouldDisplaySuggestions() {
    return (
      !this.isCareGiverSelected && this.queryCareGivers.lastSuccessful?.value
    );
  }

  override get validations(): ValidatorMap {
    return this.journalEventClass.validations(this.intl);
  }

  override afterCancel = (): Transition => {
    const targetRoute = this.session.patient
      ? 'patient-web.protected.consult.medical-synthesis'
      : 'medics-web.protected.patients.patient.administrative';

    return this.router.replaceWith(targetRoute);
  };

  override afterCompleted = (): Transition => {
    const targetRoute = this.session.patient
      ? 'patient-web.protected.consult.medical-synthesis'
      : 'medics-web.protected.patients.patient.administrative';

    return this.router.replaceWith(targetRoute);
  };

  onCareGiverSelected = (index: number) => {
    this.isCareGiverSelected = true;
    this.searchedBy = undefined;

    if (!this.queryCareGivers.lastSuccessful?.value) return;

    const careGiver = this.queryCareGivers.lastSuccessful.value[
      index
    ] as CareGiver;

    this.changeset.set('lastName', careGiver.lastName);
    this.changeset.set('firstName', careGiver.firstName);
    this.changeset.set('role', careGiver.role);
    this.changeset.set('email', careGiver.email);
    fillPhoneInput(careGiver.phone);
    this.changeset.set('address', careGiver.address);
    this.changeset.set('comments', careGiver.comments);
  };

  queryCareGivers = restartableTask(async (property: string, event: Event) => {
    this.isCareGiverSelected = false;
    this.searchedBy = property;

    const query = (event.target as HTMLInputElement).value;
    if (query.length < 1) return [];

    // Bounce search
    await timeout(400);

    const filterObject = {
      health_centre_id: this.args.patient.healthCentre.id
    };
    if (property == 'lastName') {
      Object.assign(filterObject, { last_name: query });
    } else {
      Object.assign(filterObject, { first_name: query });
    }

    const careGivers = await this.store.query('care-giver', {
      filter: filterObject
    });

    // casting as unknown as this.store.query returns a type DS.RecordArray instead of directly CareGiver, but types are actually the same
    return careGivers as unknown as CareGiver[];
  });

  override async save(): Promise<CareGiverAddedJournalEvent> {
    const je = await super.save();

    // We make sure we get the computed referent-lab
    // which is executed by PR-API every time
    // we save a care giver which may be a referent laboratory
    if (je.isValidLaboratory) {
      await this.fetcher.query('referent-lab-updated-journal-event', {
        filter: {
          patient_id: this.args.patient.id
        }
      });
    }

    return je;
  }

  <template>
    <Provider @forcComponent={{this}}>
      <:content as |Form|>
        <div class='ui info message'>
          Note : ce membre n'aura pas accès à l'outil
          <ProductName />.
        </div>

        <div class='field'>
          <Form.Dropdown
            @labelName='Rôle'
            @placeholder='Rôle du membre'
            @propertyName='role'
            @isMandatory={{true}}
            @allowAdditions={{true}}
            as |dropdown|
          >
            {{#each this.roleOptions as |role|}}
              <dropdown.Item
                @key={{role}}
                @value={{tWithDefault (concat 'careGiver.role.' role) role}}
              />
            {{/each}}
          </Form.Dropdown>
        </div>

        <div class='fields'>
          <div class='field max-w-[50%] shrink-0 grow basis-auto'>
            {{#if this.patient.healthCentre.isgt}}

              <SearchableField
                @changeset={{this.changeset}}
                @name='Nom'
                @propertyName='lastName'
                @isMandatory={{true}}
                @query={{this.queryCareGivers}}
                @onSelected={{this.onCareGiverSelected}}
                @suggestionsAreVisible={{and
                  this.shouldDisplaySuggestions
                  (eq this.searchedBy 'lastName')
                }}
              >
                <:loader>
                  {{#if
                    (and
                      this.queryCareGivers.isRunning
                      (eq this.searchedBy 'lastName')
                    )
                  }}
                    <div class='ui mini active loader inline'></div>
                  {{/if}}
                </:loader>
              </SearchableField>
            {{else}}
              <Form.Input
                @labelName='Nom *'
                @placeholder='Nom'
                @propertyName='lastName'
              />
            {{/if}}
          </div>

          <div class='field max-w-[50%] shrink-0 grow basis-auto'>
            {{#if this.patient.healthCentre.isgt}}
              <SearchableField
                @changeset={{this.changeset}}
                @name='Prénom'
                @propertyName='firstName'
                @query={{this.queryCareGivers}}
                @onSelected={{this.onCareGiverSelected}}
                @suggestionsAreVisible={{and
                  this.shouldDisplaySuggestions
                  (eq this.searchedBy 'firstName')
                }}
              >
                <:loader>
                  {{#if
                    (and
                      this.queryCareGivers.isRunning
                      (eq this.searchedBy 'firstName')
                    )
                  }}
                    <div class='ui mini active loader inline'></div>
                  {{/if}}
                </:loader>
              </SearchableField>
            {{else}}
              <Form.Input
                @labelName='Prénom'
                @placeholder='Prénom'
                @propertyName='firstName'
              />
            {{/if}}
          </div>
        </div>

        <div class='two fields'>
          <div class='field'>
            <Form.Phone
              @labelName='Téléphone'
              @propertyName='phone'
              @placeholder='01 02 34 56 78'
            />
          </div>

          <div class='field'>
            <Form.Input
              @labelName='Email'
              @placeholder='nom@example.com'
              @propertyName='email'
            />
          </div>
        </div>

        <div class='field'>
          <Form.Input
            @labelName='Adresse'
            @placeholder='21 rue du Chemin Neuf, 21200 Ruffey'
            @propertyName='address'
          />
        </div>

        <div class='field'>
          <Form.TextArea
            @labelName='Informations complémentaires'
            @propertyName='comments'
          />
        </div>
      </:content>
    </Provider>
  </template>
}
